import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, User, FileText, MoreHorizontal, UserPlus, BarChart3 } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Project, ProfileType } from '../../types';
import { useAuthStore } from '../../store/authStore';

interface ProjectCardProps {
  project: Project;
  onAssignAdvisor?: (project: Project) => void;
}

export const ProjectCard: React.FC<ProjectCardProps> = ({ project, onAssignAdvisor }) => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [isHovered, setIsHovered] = React.useState(false);

  const getStatusColor = (status: string) => {
    const colors = {
      A_PREPARER: 'bg-gray-100 text-gray-800',
      SOUMIS: 'bg-blue-100 text-blue-800',
      ACCORD_PROVISOIRE: 'bg-yellow-100 text-yellow-800',
      VALIDE: 'bg-green-100 text-green-800',
      DEBLOCAGE_FONDS: 'bg-purple-100 text-purple-800',
      EN_COURS: 'bg-indigo-100 text-indigo-800',
      VERIFICATION: 'bg-orange-100 text-orange-800',
      TERMINE: 'bg-emerald-100 text-emerald-800',
      REJETE: 'bg-red-100 text-red-800',
      SUSPENDU: 'bg-gray-100 text-gray-800'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status: string) => {
    const labels = {
      A_PREPARER: 'À préparer',
      SOUMIS: 'Soumis',
      ACCORD_PROVISOIRE: 'Accord provisoire',
      VALIDE: 'Validé',
      DEBLOCAGE_FONDS: 'Déblocage fonds',
      EN_COURS: 'En cours',
      VERIFICATION: 'Vérification',
      TERMINE: 'Terminé',
      REJETE: 'Rejeté',
      SUSPENDU: 'Suspendu'
    };
    return labels[status as keyof typeof labels] || status;
  };

  const getDomainLabel = (domain: string) => {
    const labels = {
      AGRICULTURE: 'Agriculture',
      ARTISANAT: 'Artisanat',
      COMMERCE: 'Commerce',
      SERVICES: 'Services',
      INDUSTRIE: 'Industrie',
      TOURISME: 'Tourisme',
      CULTURE: 'Culture',
      FORMATION: 'Formation',
      SOCIAL: 'Social',
      ENVIRONNEMENT: 'Environnement'
    };
    return labels[domain as keyof typeof labels] || domain;
  };

  const completedSteps = project.steps?.filter(step => step.status === 'TERMINE').length || 0;
  const totalSteps = project.steps?.length || 0;
  const progressPercentage = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;

  const canAssignAdvisor = user && [ProfileType.COORDINATEUR, ProfileType.ADMIN].includes(user.profile_type as ProfileType);
  const needsAdvisor = !project.assigned_advisor_id;
  return (
    <div 
      className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 hover:shadow-2xl hover:scale-105 hover:-translate-y-2 transition-all duration-300 cursor-pointer group animate-slide-up"
      onClick={() => navigate(`/projects/${project.id}`)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1 line-clamp-1 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-all duration-300 group-hover:scale-105">
            {project.name}
          </h3>
          <span className="text-sm text-blue-600 dark:text-blue-400 font-medium transition-all duration-200 group-hover:font-semibold">
            {getDomainLabel(project.domain)}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <span className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 ${getStatusColor(project.status)} ${isHovered ? 'animate-pulse' : ''}`}>
            {getStatusLabel(project.status)}
          </span>
          <div className="flex items-center space-x-1">
            {canAssignAdvisor && needsAdvisor && onAssignAdvisor && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onAssignAdvisor(project);
                  }}
                  className="p-1 hover:bg-blue-100 dark:hover:bg-blue-900/20 rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100 hover:scale-110"
                  title="Assigner un accompagnateur"
                >
                  <UserPlus className="w-4 h-4 text-blue-600" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/projects/${project.id}/business-plan`);
                  }}
                  className="p-1 hover:bg-blue-100 dark:hover:bg-blue-900/20 rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100 hover:scale-110"
                  title="Business Plan IA"
                >
                  <BarChart3 className="w-4 h-4 text-blue-600" />
                </button>
              </>
            )}
            <button className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100 hover:scale-110 hover:rotate-90">
              <MoreHorizontal className="w-4 h-4 text-gray-400 dark:text-gray-500" />
            </button>
          </div>
        </div>
      </div>

      {/* Description */}
      {project.description && (
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-2">
          {project.description}
        </p>
      )}

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2 transition-colors duration-200">
          <span>Progression</span>
          <span>{completedSteps}/{totalSteps} étapes</span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden shadow-inner">
          <div 
            className="bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-500 dark:to-indigo-500 h-3 rounded-full transition-all duration-700 ease-out shadow-sm"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>

      {/* Footer Info */}
      <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 transition-colors duration-200">
        <div className="flex items-center space-x-4">
          {project.assigned_advisor ? (
            <div className="flex items-center space-x-1 hover:text-gray-700 dark:hover:text-gray-300 transition-colors duration-200">
              <User className="w-4 h-4" />
              <span>{project.assigned_advisor.full_name || project.assigned_advisor.email}</span>
            </div>
          ) : canAssignAdvisor && (
            <div className="flex items-center space-x-1 text-orange-600 dark:text-orange-400">
              <UserPlus className="w-4 h-4" />
              <span>Non assigné</span>
            </div>
          )}
          {project.documents && project.documents.length > 0 && (
            <div className="flex items-center space-x-1 hover:text-gray-700 dark:hover:text-gray-300 transition-colors duration-200">
              <FileText className="w-4 h-4" />
              <span>{project.documents.length}</span>
            </div>
          )}
        </div>
        <div className="flex items-center space-x-1 hover:text-gray-700 dark:hover:text-gray-300 transition-colors duration-200">
          <Calendar className="w-4 h-4" />
          <span>{format(new Date(project.created_at), 'dd MMM yyyy', { locale: fr })}</span>
        </div>
      </div>

      {/* Funding Info */}
      {(project.funding_requested || project.funding_amount) && (
        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 transition-colors duration-200">
          <div className="flex justify-between text-sm">
            {project.funding_requested && (
              <div>
                <span className="text-gray-500 dark:text-gray-400 transition-colors duration-200">Financement demandé:</span>
                <span className="ml-2 font-medium text-gray-900 dark:text-gray-100 transition-colors duration-200">
                  {project.funding_requested.toLocaleString('fr-FR')} €
                </span>
              </div>
            )}
            {project.funding_amount && (
              <div>
                <span className="text-gray-500 dark:text-gray-400 transition-colors duration-200">Accordé:</span>
                <span className="ml-2 font-medium text-green-600 dark:text-green-400 transition-colors duration-200">
                  {project.funding_amount.toLocaleString('fr-FR')} €
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};